import { Grid } from '../../core/Grid.js';
import { Position, positionKey, parsePositionKey } from '../../core/GridGeometry.js';
import { JumpsGame } from './JumpsGame.js';
import { VortexGateManager } from '../VortexGateManager.js';
import { IVortexGame } from '../IVortexGame.js';

/**
 * Vortex variant of Jumps.
 *
 * An active gate acts on the **ramp body**: the jumper walking a ramp enters a gate and
 * continues from its pair, so a gated ramp may span two quarters — the one exception to
 * "a ramp lies within one quarter". Clicking a gate while building steps onto it and out
 * of its pair in a single move.
 *
 * Nearly all of that lives in the base game, expressed through the `gatePairFor` hook; the
 * only genuinely Vortex-side rules are what may be activated and what a deactivation
 * breaks.
 */
export class JumpsVortex extends JumpsGame implements IVortexGame {
    private readonly gm = new VortexGateManager();

    constructor(grid: Grid) {
        super(grid);
    }

    setVortexData(gatePairs: Array<[Position, Position]>, allGates: Set<string>): void {
        this.gm.setVortexData(gatePairs, allGates, () => {
            // Deleting a pair in the Hub retires it without any toggle, so this is the
            // *only* signal that a ramp routed through it has just been severed.
            this.pruneDisconnectedRamps();
        });
    }

    isGateCell(pos: Position): boolean { return this.gm.isGateCell(pos); }
    isActiveGate(pos: Position): boolean { return this.gm.isActiveGate(pos); }

    canActivateGate(pos: Position): boolean {
        return this.gm.canActivateGate(pos, (p, paired) =>
            !this.getRampAtCell(p) && !this.getRampAtCell(paired));
    }

    toggleVortexGate(pos: Position): boolean {
        const toggled = this.gm.toggleVortexGate(pos, this.canActivateGate(pos));
        // Switching a gate off severs any ramp routed through it.
        if (toggled) this.pruneDisconnectedRamps();
        return toggled;
    }

    getActiveGatePairs(): Map<string, string> { return this.gm.getActiveGatePairs(); }
    getVortexGatePairs(): Array<[Position, Position]> { return this.gm.getVortexGatePairs(); }
    getAllGatePositions(): Set<string> { return this.gm.getAllGatePositions(); }

    /** The single hook the base game routes every gate rule through. */
    protected override gatePairFor(pos: Position): Position | null {
        if (!this.gm.hasActiveGates()) return null;
        const pairedKey = this.gm.raw.get(positionKey(pos));
        return pairedKey ? parsePositionKey(pairedKey) : null;
    }

    override reset(): void {
        super.reset();
        this.gm.clearActiveGates();
    }

    override isStarted(): boolean {
        return super.isStarted() || this.gm.hasActiveGates();
    }

    override serialize(): string {
        return this.gm.serializeWithBase(super.serialize(), this.gs);
    }

    override deserialize(serialized: string): void {
        this.gm.clearActiveGates();
        if (!serialized) {
            super.deserialize('');
            return;
        }
        const { basePart, gatePart } = VortexGateManager.splitSerialized(serialized);
        super.deserialize(basePart);
        this.gm.deserializeGates(gatePart, this.gs);
    }
}
